import React from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { AnalyticsFilters } from "../types/analytics.types";
import { useGrowthAnalytics } from "../hooks/useAnalytics";
import { StatCard, ChartCard, LoadingState, ErrorState, EmptyState } from "../design/ui";
import {
  lineOptions,
  barOptions,
  doughnutOptions,
  lineDataset,
  barDataset,
  doughnutDataset,
  formatPercent,
} from "../design/chartTheme";

interface Props {
  filters: AnalyticsFilters;
}

// Order streak buckets low → high regardless of object key order
const STREAK_ORDER = ["0", "1-2", "3-6", "7-13", "14-29", "30+"];

export const GrowthTab: React.FC<Props> = ({ filters }) => {
  const { data, loading, error, refetch } = useGrowthAnalytics(filters);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} />;
  if (!data) return <EmptyState />;

  const { signups, subscriptions, streaks, languages } = data;

  const signupLabels = signups.daily.map((d) => d.date.slice(5));
  const signupChart = {
    labels: signupLabels,
    datasets: [
      barDataset(
        "New signups",
        signups.daily.map((d) => d.signups),
        2
      ),
    ],
  };
  const cumulativeChart = {
    labels: signupLabels,
    datasets: [
      lineDataset(
        "Total users",
        signups.daily.map((d) => d.cumulative_users),
        0,
        true
      ),
    ],
  };

  const subEntries = Object.entries(subscriptions.by_status).sort((a, b) => b[1] - a[1]);
  const subChart = {
    labels: subEntries.map(([status]) => status),
    datasets: [doughnutDataset(subEntries.map(([, count]) => count))],
  };

  const streakLabels = STREAK_ORDER.filter((b) => b in streaks.buckets);
  const streakChart = {
    labels: streakLabels.map((b) => `${b} days`),
    datasets: [
      barDataset(
        "Users",
        streakLabels.map((b) => streaks.buckets[b]),
        3
      ),
    ],
  };

  const langEntries = Object.entries(languages)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  return (
    <div>
      <div className="an-stat-grid">
        <StatCard
          label="New Signups"
          value={signups.new_in_range.toLocaleString()}
          sub="In selected range"
        />
        <StatCard
          label="Signups (7d)"
          value={signups.new_last_7_days.toLocaleString()}
          sub={`${signups.new_last_30_days.toLocaleString()} in 30d`}
        />
        <StatCard
          label="Active Paid"
          value={subscriptions.active_paid.toLocaleString()}
          sub={`${subscriptions.expiring_within_7_days} expiring in 7d`}
        />
        <StatCard
          label="Conversion Rate"
          value={formatPercent(subscriptions.conversion_rate * 100)}
          sub="Paid / all users"
        />
        <StatCard
          label="Active Streaks"
          value={streaks.users_with_active_streak.toLocaleString()}
          sub={`Avg ${streaks.avg_active_streak.toFixed(1)} days`}
        />
        <StatCard
          label="Longest Streak"
          value={`${streaks.max_streak} days`}
          sub="Across all users"
        />
        <StatCard
          label="Active (7d)"
          value={signups.active_last_7_days.toLocaleString()}
          sub="Opened app recently"
        />
        <StatCard
          label="Auto-Renew On"
          value={subscriptions.auto_renew_on.toLocaleString()}
          sub="Of paid users"
        />
      </div>

      <div className="an-chart-grid">
        <ChartCard title="New Signups" subtitle="Accounts created per day" height={280}>
          {signups.daily.length ? (
            <Bar data={signupChart} options={barOptions()} />
          ) : (
            <EmptyState title="No signups in range" message="" />
          )}
        </ChartCard>
        <ChartCard title="Cumulative Users" subtitle="Total accounts over time" height={280}>
          {signups.daily.length ? (
            <Line data={cumulativeChart} options={lineOptions()} />
          ) : (
            <EmptyState title="No data" message="" />
          )}
        </ChartCard>
      </div>

      <div className="an-chart-grid">
        <ChartCard title="Subscription Mix" subtitle="Current status across all users" height={280}>
          <Doughnut data={subChart} options={doughnutOptions()} />
        </ChartCard>
        <ChartCard title="Streak Distribution" subtitle="The core retention mechanic" height={280}>
          <Bar
            data={streakChart}
            options={barOptions({ plugins: { legend: { display: false } } })}
          />
        </ChartCard>
      </div>

      <ChartCard title="Learners by Language" subtitle="Active target language">
        <div className="an-ranklist">
          {langEntries.map(([lang, count], i) => (
            <div key={lang} className="an-rankrow">
              <span className="an-rank-num">{i + 1}</span>
              <span className="an-rank-name">{lang}</span>
              <span className="an-rank-value">{count.toLocaleString()} users</span>
            </div>
          ))}
        </div>
      </ChartCard>

      {signups.unknown_creation_dates > 0 && (
        <p className="an-header-sub" style={{ marginTop: 12 }}>
          Note: {signups.unknown_creation_dates.toLocaleString()} users have no recorded signup date
          (created before tracking) and are counted in totals but not the daily signup series.
        </p>
      )}
    </div>
  );
};

export default GrowthTab;
